<script lang="ts" setup>
import { computed, ref, watch } from 'vue';

import { i18n } from '@apps/main/modules/localization/localizationSetup';

import type {
  FerryAutoCompleteSuggestion,
  FlightAutoCompleteSuggestion,
  PortInfo,
} from '@libs/trip-planner-interfaces';
import { TransportType } from '@libs/trip-planner-interfaces';
import {
  AppFormButton,
  AppStandardModal,
  AppToggle,
  AppMessageBox,
  AppSvgIcon,
  AppReturnFadeDownTransition,
} from '@libs/ui';

import { AirportSelector } from '../../../../../modules/airportSelector';
import { SeaportSelector } from '../../../../../modules/seaportSelector';
import { FerryRouteItem } from '../../../../../modules/ferryRouteItem';
import type {
  PostSelectionPorts,
  PostSelectionSeaports,
  PortSelectionUpdatePayload,
} from '../../../models';

interface PortSelectionModalProps {
  transportType: TransportType.Flight | TransportType.Ferry;
  initialFromPort?: PortInfo;
  initialToPort?: PortInfo;
  canAddReturnIngredient: boolean;
  ferrySuggestions?: FerryAutoCompleteSuggestion[];
}

const props = defineProps<PortSelectionModalProps>();
interface PortSelectionModalEmits {
  (eventName: 'close', context: void): void;
  (eventName: 'confirm', payload: PortSelectionUpdatePayload): void;
}

const initialPort: PortInfo = {
  code: '',
  label: '',
  name: '',
};

const modalState = ref<PostSelectionPorts>({
  from: initialPort,
  to: initialPort,
  transportType: props.transportType,
  allowMultipleVehicles: false,
  allowVehicles: true,
});

const shouldAddReturnIngredient = ref(true);

watch(
  props,
  () => {
    if (props.initialFromPort) {
      modalState.value.from = props.initialFromPort;
    }
    if (props.initialToPort) {
      modalState.value.to = props.initialToPort;
    }
  },
  { immediate: true }
);

const emit = defineEmits<PortSelectionModalEmits>();

const { t } = i18n.global;

const isEditingAirports = ref<boolean>(false);
const isEditingSeaport = ref<boolean>(false);
const isEditingFromAirport = ref<boolean>(false);

const modalTitle = computed<string>(() => {
  if (props.transportType === TransportType.Ferry) {
    return t('planner.transport.modal.ferry.header');
  }

  if (isEditingAirports.value) {
    return t('planner.transport.modal.flight.header_autocomplete');
  }
  return t('planner.transport.modal.flight.header');
});

const addReturnToggleLable = computed(() =>
  props.transportType === TransportType.Flight
    ? t('planner.return_flight')
    : t('planner.return_ferry')
);

const ferryPlaceholderText = computed(() => {
  if (modalState.value.from.code && modalState.value.to.code) {
    return `${modalState.value.from.label} - ${modalState.value.to.label}`;
  }
  return t('planner.transport.modal.ferry.placeholder');
});

const isButtonDisabled = computed<boolean>(
  () =>
    !modalState.value.from.code ||
    !modalState.value.to.code ||
    modalState.value.from.code === modalState.value.to.code
);

const shouldShowValidationMessage = computed<boolean>(
  () =>
    modalState.value.from.code === modalState.value.to.code &&
    !!modalState.value.from.code &&
    !!modalState.value.to.code
);

const shouldShowFerrySuggestions = computed<boolean>(
  () =>
    props.transportType === TransportType.Ferry &&
    !isEditingSeaport.value &&
    props.ferrySuggestions !== undefined &&
    props.ferrySuggestions.length > 0 &&
    !(modalState.value.from.code && modalState.value.to.code)
);

function handleClose(): void {
  if (isEditingAirports.value) {
    isEditingAirports.value = false;
    return;
  }

  if (isEditingSeaport.value) {
    isEditingSeaport.value = false;
    return;
  }
  emit('close');
}

function handleConfirm(): void {
  const payload: PortSelectionUpdatePayload = {
    ports: modalState.value,
    addReturnTransport:
      props.canAddReturnIngredient && shouldAddReturnIngredient.value,
  };

  emit('confirm', payload);
}

function openAirportSelection(isDepatureAirport: boolean): void {
  isEditingAirports.value = true;
  isEditingFromAirport.value = isDepatureAirport;
}

function openSeaportSelection(): void {
  isEditingSeaport.value = true;
}

function updateAirport(airport: FlightAutoCompleteSuggestion): void {
  if (isEditingFromAirport.value) {
    modalState.value.from = {
      code: airport.iata,
      label: airport.label,
      name: airport.tooltip,
    };
  } else {
    modalState.value.to = {
      code: airport.iata,
      label: airport.label,
      name: airport.tooltip,
    };
  }
  isEditingAirports.value = false;
}

function updateSeaport(event: FerryAutoCompleteSuggestion): void {
  const postSelectionSeaport: PostSelectionSeaports = {
    transportType: TransportType.Ferry,
    from: {
      code: event.data.from.code,
      label: event.data.from.label,
      name: event.data.from.country,
    },
    to: {
      code: event.data.to.code,
      label: event.data.to.label,
      name: event.data.to.country,
    },
    defaultVehicle: event.data.defaultVehicleId,
    allowMultipleVehicles: event.data.allowMultipleVehicles,
    allowVehicles: event.data.allowVehicles,
  };
  modalState.value = postSelectionSeaport;

  isEditingSeaport.value = false;
}

function setReturnToggleValue(event: boolean): void {
  shouldAddReturnIngredient.value = event;
}
</script>

<template>
  <AppStandardModal
    :is-button-disabled="isButtonDisabled"
    @close="handleClose"
    @confirm="handleConfirm"
  >
    <template #title>{{ modalTitle }}</template>

    <template #body>
      <div class="port-selection-modal-body">
        <template v-if="props.transportType === TransportType.Ferry">
          <div v-if="isEditingSeaport">
            <SeaportSelector
              :initial-ferry-route="modalState.from.label"
              @ferry-route-selected="updateSeaport($event)"
            />
          </div>
          <div v-else class="ferry-selection">
            <AppFormButton
              class="airport-selection-button"
              size="xxl"
              :wide="true"
              :icon="{ name: 'ferry' }"
              text-align="align-left"
              @click="openSeaportSelection()"
            >
              {{ ferryPlaceholderText }}
            </AppFormButton>
          </div>
        </template>
        <template v-if="props.transportType === TransportType.Flight">
          <div v-if="isEditingAirports" class="airport-autocomplete">
            <AirportSelector
              :initial-airport="
                isEditingFromAirport
                  ? modalState.from?.label
                  : modalState.to?.label
              "
              @airport-selected="updateAirport($event)"
            />
          </div>
          <div v-else>
            <AppMessageBox
              v-if="shouldShowValidationMessage"
              :text="$t('modals.addFlights.different_airports_validation')"
              message-type="tertiary"
              class="message-box"
            >
              <template #left-icon>
                <AppSvgIcon name="infoCircle" color="color-secondary-40" />
              </template>
            </AppMessageBox>
            <div class="airports-selection">
              <AppFormButton
                class="airport-selection-button"
                size="xxl"
                :wide="true"
                :icon="{ name: 'airplaneTakeoff' }"
                text-align="align-left"
                @click="openAirportSelection(true)"
              >
                <template v-if="modalState.from?.code">
                  {{ modalState.from.label }}
                </template>
                <template v-else>
                  {{ t('planner.transport.choose_departing_airport') }}
                </template>
              </AppFormButton>
              <AppFormButton
                class="airport-selection-button"
                size="xxl"
                :wide="true"
                :icon="{ name: 'airplaneLanding' }"
                text-align="align-left"
                @click="openAirportSelection(false)"
              >
                <template v-if="modalState.to?.code">
                  {{ modalState.to.label }}
                </template>
                <template v-else>
                  {{ t('planner.transport.choose_arriving_airport') }}
                </template>
              </AppFormButton>
            </div>
          </div>
        </template>
        <div
          v-if="
            props.canAddReturnIngredient &&
            !isEditingAirports &&
            !isEditingSeaport
          "
          class="add-return-ingredient"
        >
          <AppToggle
            :checked="shouldAddReturnIngredient"
            @toggle="setReturnToggleValue"
          >
            <span class="toggle-title">
              {{ addReturnToggleLable }}
            </span>
          </AppToggle>
        </div>
        <AppReturnFadeDownTransition>
          <div v-if="shouldShowFerrySuggestions" class="ferry-suggestions">
            <div class="title">
              {{ $t('planner.transport.modal.ferry.suggested') }}
            </div>
            <FerryRouteItem
              v-for="(item, index) in props.ferrySuggestions"
              :key="index"
              :item="item"
              @click="updateSeaport(item)"
            />
          </div>
        </AppReturnFadeDownTransition>
      </div>
    </template>

    <template #button>
      {{ $t('planner.done') }}
    </template>
  </AppStandardModal>
</template>

<style lang="scss" scoped>
.port-selection-modal-body {
  .airports-selection {
    display: flex;
    flex-direction: column;

    justify-content: center;
    position: relative;
    gap: var(--spacing-16);

    &:before {
      position: absolute;
      z-index: 1;
      content: '';
      height: 90%;
      width: 1px;
      margin-top: var(--spacing-8);
      margin-left: calc(var(--spacing-24) + var(--spacing-12));
      border-left: 1px dotted var(--color-secondary-70);
    }

    .airport-selection-button {
      position: relative;
      z-index: 2;
    }
  }

  .add-return-ingredient {
    margin-top: var(--spacing-24);
    .toggle-title {
      color: var(--color-secondary-40);
      font: var(--font-label-l-bold);
      margin-bottom: var(--spacing-4);
    }
  }

  .ferry-suggestions {
    * {
      box-sizing: border-box;
    }
    .title {
      padding-top: var(--spacing-24);
      padding-bottom: var(--spacing-8);
      color: var(--color-secondary-60);
      font: var(--font-label-s-bold);
    }
    .ferry-route-item {
      margin-bottom: var(--spacing-16);
      @include on-hover {
        background-color: var(--color-secondary-97);
      }
    }
  }

  .message-box {
    justify-content: flex-start;
    margin-bottom: var(--spacing-24);
  }
}
</style>
